import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  BookOpen, 
  LogOut, 
  FileText, 
  CheckCircle2, 
  Clock, 
  Trophy,
  ArrowRight,
  Eye,
  Folder,
  FolderOpen,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import PassageReader from '../components/PassageReader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPassageModal, setShowPassageModal] = useState(false);
  const [selectedPassage, setSelectedPassage] = useState({ title: '', text: '' });

  const handleViewPassage = (quiz, e) => {
    e.stopPropagation();
    setSelectedPassage({ title: quiz.quiz_title, text: quiz.passage_text });
    setShowPassageModal(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    const studentData = localStorage.getItem('student_data');
    
    if (!token || !studentData) {
      navigate('/student/login');
      return;
    }
    
    setStudent(JSON.parse(studentData));
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [assignmentsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/student/assignments`, { headers }),
        axios.get(`${API}/student/results`, { headers })
      ]);
      
      setAssignments(assignmentsRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_data');
    navigate('/student/login');
    toast.info('Logged out');
  };

  const pendingQuizzes = assignments.filter(a => !a.completed);
  const completedQuizzes = assignments.filter(a => a.completed);
  const averageScore = results.length > 0 
    ? Math.round(results.reduce((sum, r) => sum + r.percentage, 0) / results.length) 
    : 0;

  // Group quizzes by subject
  const groupBySubject = (quizzes) => {
    const grouped = {};
    quizzes.forEach(quiz => {
      const subject = quiz.subject || 'General';
      if (!grouped[subject]) {
        grouped[subject] = [];
      }
      grouped[subject].push(quiz);
    });
    // Sort subjects alphabetically
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {});
  };

  const pendingBySubject = groupBySubject(pendingQuizzes);
  const completedBySubject = groupBySubject(completedQuizzes);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-white border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Skeleton className="h-8 w-40" />
          </div>
        </nav>
        <main className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-xl font-semibold text-primary">HomeClass</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Hi, <span className="font-medium text-foreground">{student?.first_name}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2"
                data-testid="student-logout"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Welcome */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
            Welcome back, {student?.first_name}!
          </h1>
          <p className="text-muted-foreground text-lg">
            {pendingQuizzes.length > 0 
              ? `You have ${pendingQuizzes.length} quiz${pendingQuizzes.length > 1 ? 'zes' : ''} to complete`
              : 'Great job! No pending quizzes right now.'}
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Pending</span>
                </div>
                <p className="text-3xl font-serif font-medium text-primary" data-testid="pending-count">
                  {pendingQuizzes.length}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Completed</span>
                </div>
                <p className="text-3xl font-serif font-medium text-primary" data-testid="completed-count">
                  {completedQuizzes.length}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">Average Score</span>
                </div>
                <p className="text-3xl font-serif font-medium text-primary" data-testid="average-score">
                  {averageScore}%
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Pending Quizzes by Subject */}
        {pendingQuizzes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-10"
          >
            <h2 className="font-serif text-xl font-medium text-primary mb-4">Quizzes to Complete</h2>
            
            <Accordion type="multiple" defaultValue={Object.keys(pendingBySubject)} className="space-y-3">
              {Object.entries(pendingBySubject).map(([subject, quizzes]) => (
                <AccordionItem 
                  key={subject} 
                  value={subject}
                  className="border rounded-lg bg-card overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Folder className="w-5 h-5 text-accent" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">{subject}</span>
                        <Badge variant="secondary" className="ml-2">
                          {quizzes.length} quiz{quizzes.length !== 1 ? 'zes' : ''}
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                      {quizzes.map((quiz, index) => (
                        <Card
                          key={quiz.assignment_id}
                          className="border-border hover:border-accent/40 hover:shadow-md transition-all cursor-pointer group"
                          onClick={() => navigate(`/student/take/${quiz.assignment_id}`)}
                          data-testid={`pending-quiz-${subject}-${index}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-accent" />
                                <h3 className="font-medium text-primary group-hover:text-accent transition-colors">
                                  {quiz.quiz_title}
                                </h3>
                              </div>
                              {quiz.show_passage && quiz.passage_text && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 gap-1 text-xs hover:bg-primary/10"
                                  onClick={(e) => handleViewPassage(quiz, e)}
                                >
                                  <Eye className="w-3 h-3" />
                                  Passage
                                </Button>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {quiz.grade_level} • {quiz.question_count} questions
                              </span>
                              <Button size="sm" className="h-7 gap-1 bg-accent hover:bg-accent/90">
                                Start
                                <ArrowRight className="w-3 h-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        )}

        {/* Completed Quizzes by Subject */}
        {completedQuizzes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className="font-serif text-xl font-medium text-primary mb-4">Completed Quizzes</h2>
            
            <Accordion type="multiple" className="space-y-3">
              {Object.entries(completedBySubject).map(([subject, quizzes]) => (
                <AccordionItem 
                  key={subject} 
                  value={subject}
                  className="border rounded-lg bg-card overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">{subject}</span>
                        <Badge variant="secondary" className="ml-2">
                          {quizzes.length} completed
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-2 pt-2">
                      {quizzes.map((quiz, index) => {
                        const result = results.find(r => r.quiz_id === quiz.quiz_id);
                        return (
                          <div
                            key={quiz.assignment_id}
                            className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                            data-testid={`completed-quiz-${subject}-${index}`}
                          >
                            <div className="flex items-center gap-3">
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              <div>
                                <p className="font-medium text-sm">{quiz.quiz_title}</p>
                                <p className="text-xs text-muted-foreground">{quiz.grade_level}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-primary">
                                {quiz.score || result?.percentage || 0}%
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {result?.correct_answers || 0}/{result?.total_questions || quiz.question_count}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        )}

        {/* Empty State */}
        {pendingQuizzes.length === 0 && completedQuizzes.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-medium text-primary mb-2">No quizzes yet</h3>
            <p className="text-muted-foreground">
              Your teacher hasn't assigned any quizzes yet. Check back soon!
            </p>
          </motion.div>
        )}
      </main>

      {/* Passage Modal */}
      <Dialog open={showPassageModal} onOpenChange={setShowPassageModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reading Passage
            </DialogTitle>
            <DialogDescription className="sr-only">
              Read the passage carefully before answering the quiz questions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <h3 className="font-medium text-lg mb-4">{selectedPassage.title}</h3>
            <ScrollArea className="h-[50vh] pr-4">
              <PassageReader text={selectedPassage.text} />
            </ScrollArea>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setShowPassageModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;
